<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useTheme } from '../composables/useTheme';
import HrListItem from './HrListItem.vue';

/**
 * Navigation item — a single M3-styled destination row for a side nav drawer.
 *
 * @status recommended
 * @group navigation
 * @useWhen A navigation destination inside a side drawer
 * @avoidWhen Non-navigation list content — use HrListItem
 * @related HrNavGroup
 * @within hr-list
 * @a11y Activates on Enter/Space. Active state uses aria-current via the underlying HrListItem.
 */
defineOptions({ name: 'HrNavItem' });

const props = defineProps({
  /**
   * Destination label text.
   * @example Dashboard
   */
  label: {
    type: String,
    default: null,
  },
  /** Optional secondary text shown below the label. */
  caption: {
    type: String,
    default: null,
  },
  /**
   * Material icon name shown in the leading section.
   * @example dashboard
   */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Marks this item as the current active destination (sets aria-current and applies the active
   * indicator). Defaults to null so that, when left unset, HrNavItem derives the active state from
   * the current route against `to` (see `exact`) — passing `false` suppresses that automatic match.
   */
  active: {
    type: Boolean,
    default: null,
  },
  /** Vue Router route target — a path string or location object. */
  to: {
    type: [String, Object],
    default: null,
  },
  /**
   * URL for external link navigation.
   * Must default to `undefined` (not `null`) — Quasar's router-link detection
   * checks `href !== void 0`, so a `null` default leaks through the prop chain
   * and disables Vue Router navigation even when only `to` is provided.
   */
  href: {
    type: String,
    default: undefined,
  },
  /**
   * Match the destination route exactly. When false (the default) the item is also active on
   * nested/descendant paths — e.g. the `/history` item stays highlighted on `/history/timeline`
   * or `/history/:id`. Set `exact` for destinations whose path is a prefix of everything (Home, `/`).
   */
  exact: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the item. */
  disable: {
    type: Boolean,
    default: false,
  },
});

const { hasTheme } = useTheme();
const route = useRoute();
const router = useRouter();

/**
 * Active-state resolution. An explicit `active` from the caller always wins. Otherwise we derive it
 * from the current route against `to`: `exact` matches only the exact path; the default (inclusive)
 * also matches descendant paths. This is needed because the app's sub-pages are flat sibling routes
 * (e.g. /history/timeline is not nested under /history), so Vue Router's own record-based active
 * matching never marks the parent destination active on a sub-page.
 */
const resolvedActive = computed(() => {
  if (props.active !== null && props.active !== undefined) {
    return props.active;
  }
  if (props.to === null || props.to === undefined || props.to === '') {
    return null;
  }

  let targetPath;
  try {
    targetPath = router.resolve(props.to).path;
  } catch {
    return null;
  }

  const current = route.path.endsWith('/') && route.path.length > 1
    ? route.path.slice(0, -1)
    : route.path;
  if (props.exact) {
    return current === targetPath;
  }

  const base = targetPath.endsWith('/') ? targetPath.slice(0, -1) : targetPath;
  return current === base || current.startsWith(`${base}/`);
});
</script>

<template>
  <hr-list-item
    :class="hasTheme ? 'hr-nav-item' : undefined"
    v-bind="{ ...$props, ...$attrs }"
    :active="resolvedActive"
    :clickable="!disable"
  >
    <!-- @slot Item label — falls back to the `label` prop. -->
    <slot />
    <template
      v-if="$slots.leading"
      #leading
    >
      <!-- @slot leading Leading section (icon or avatar). -->
      <slot name="leading" />
    </template>
    <template
      v-if="$slots.trailing"
      #trailing
    >
      <!-- @slot trailing Trailing section (badge, status, or action). -->
      <slot name="trailing" />
    </template>
  </hr-list-item>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/* ── Pill shape ──────────────────────────────────────────────────── */
/*
 * M3 Navigation Drawer destinations use a full-pill active indicator
 * inset from the container edges (12px each side per M3 spec).
 * The margin keeps the pill from touching the drawer border; overflow: hidden
 * clips the state layer to the pill shape.
 */
.hr-nav-item {
  margin: 0 12px;
  border-radius: var(--app-shape-corner-full, 9999px);
  overflow: hidden;
}

/* ── Typography ──────────────────────────────────────────────────── */
/*
 * M3 nav drawer destination labels use label-large (14sp/500w),
 * not body-large (16sp/regular) which is correct for list/menu items.
 */
.hr-nav-item :deep(.hr-item-label:not(.q-item__label--caption)) {
  font-size:      var(--app-type-label-large-size);
  line-height:    var(--app-type-label-large-line-height);
  font-weight:    var(--app-type-label-large-weight);
  letter-spacing: var(--app-type-label-large-tracking);
}

</style>
